import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  ASK_IMAGE_BYTES_MAX,
  ASK_IMAGES_MAX,
  ASK_MESSAGE_MAX,
  askAskInputSchema,
  askEventSchema,
  askStatusSchema,
  type AskCodeRef,
  type AskContext,
  type AskEngineErrorCode,
  type AskEvent,
  type AskMessage,
  type AskRedirect,
  type AskRunPhase,
  type AskScope,
  type AskSource,
  type AskSpecAgreement,
  type AskStatus,
  type AskToolsetBridge,
  type AskTurnOutcome,
  type AskVerdict,
  type HelpImageInput,
} from "@ttc-spectree/toolbox-contract";
import type { HelpAttachment, HelpPersonMessage } from "@/hooks/use-help-bridge";
import {
  ASK_IMAGE_LIMIT_MESSAGE,
  ASK_IMAGE_SIZE_MESSAGE,
  ASK_IMAGE_TYPE_MESSAGE,
  askErrorMessage,
} from "@/lib/toolbox/ask";
import { isHelpImageMimeType, newHelpConversationId, readFileAsBase64 } from "@/lib/toolbox/help";

/**
 * The Product Q&A page's link to `window.spectreeToolbox.ask` (epic
 * PI-SPEC-SW-E310, feature PI-SPEC-SW-1628, ER-1667 Package F, Resolved
 * Decision 10d).
 *
 * - Readiness: `ask.getStatus()` and `status-changed` events.
 * - One conversation in page memory only, never sent to SpecTree. Answer text
 *   streams from `answer-delta` events; every event is parsed with
 *   `askEventSchema.safeParse` and ignored otherwise (a `turn-finished` that
 *   does not parse still ends the turn, as failed).
 * - Up to `ASK_IMAGES_MAX` screenshots per question (Help's limits); a fourth
 *   is refused with a plain sentence.
 * - Stop cancels the running turn; Close conversation deletes its folder on
 *   this computer (`closeConversation`) and clears the messages. Leaving the
 *   page closes it too.
 */

/** The person's question, shaped like Help's so Help's bubble shows it. */
export type AskPersonMessage = HelpPersonMessage;

export interface AskAnswerMessage {
  id: string;
  role: "ask";
  /** Raw streamed text; show it through `askDisplayText`. */
  text: string;
  streaming: boolean;
  phase?: AskRunPhase;
  outcome?: AskTurnOutcome;
  verdict?: AskVerdict;
  codeRefs: AskCodeRef[];
  sources: AskSource[];
  specAgrees?: AskSpecAgreement;
  redirect?: AskRedirect;
  errorCode?: AskEngineErrorCode;
  errorMessage?: string;
}

export type AskChatMessage = AskPersonMessage | AskAnswerMessage;

// ---------------------------------------------------------------------------
// Conversation reducer
// ---------------------------------------------------------------------------

export interface AskConversationState {
  conversationId: string;
  messages: AskChatMessage[];
  /** The answer being written, or null. */
  activeAnswerId: string | null;
}

export type AskConversationAction =
  | { type: "reset"; conversationId: string }
  | { type: "ask-started"; person: AskPersonMessage; answerId: string }
  | { type: "ask-failed"; answerId: string; errorCode: AskEngineErrorCode | undefined; message: string }
  | { type: "event"; event: Exclude<AskEvent, { type: "status-changed" }> };

function updateAnswer(
  state: AskConversationState,
  answerId: string,
  change: (answer: AskAnswerMessage) => AskAnswerMessage,
): AskChatMessage[] {
  return state.messages.map((message) => (message.role === "ask" && message.id === answerId ? change(message) : message));
}

/** Apply one conversation change. Events go to the active answer of the same conversation. Pure; exported for tests. */
export function askConversationReducer(state: AskConversationState, action: AskConversationAction): AskConversationState {
  switch (action.type) {
    case "reset":
      return { conversationId: action.conversationId, messages: [], activeAnswerId: null };
    case "ask-started": {
      const answer: AskAnswerMessage = {
        id: action.answerId,
        role: "ask",
        text: "",
        streaming: true,
        phase: "starting",
        codeRefs: [],
        sources: [],
      };
      return { ...state, messages: [...state.messages, action.person, answer], activeAnswerId: action.answerId };
    }
    case "ask-failed":
      return {
        ...state,
        activeAnswerId: state.activeAnswerId === action.answerId ? null : state.activeAnswerId,
        messages: updateAnswer(state, action.answerId, (answer) => ({
          ...answer,
          streaming: false,
          outcome: "failed",
          ...(action.errorCode ? { errorCode: action.errorCode } : {}),
          errorMessage: action.message,
        })),
      };
    case "event": {
      const { event } = action;
      if (event.conversationId !== state.conversationId || state.activeAnswerId === null) return state;
      const answerId = state.activeAnswerId;
      switch (event.type) {
        case "turn-progress":
          return {
            ...state,
            messages: updateAnswer(state, answerId, (answer) => ({ ...answer, phase: event.phase })),
          };
        case "answer-delta":
          return {
            ...state,
            messages: updateAnswer(state, answerId, (answer) => ({
              ...answer,
              text: answer.text + event.text,
              phase: "writing",
            })),
          };
        case "turn-finished":
          return {
            ...state,
            activeAnswerId: null,
            messages: updateAnswer(state, answerId, (answer) => {
              const done: AskAnswerMessage = { ...answer, streaming: false, outcome: event.outcome };
              if (event.outcome === "answered") {
                return {
                  ...done,
                  text: event.answer ?? answer.text,
                  verdict: event.verdict ?? "not-sure",
                  codeRefs: event.codeRefs,
                  sources: event.sources,
                  ...(event.specAgrees ? { specAgrees: event.specAgrees } : {}),
                  ...(event.redirect ? { redirect: event.redirect } : {}),
                };
              }
              if (event.outcome === "failed") {
                return {
                  ...done,
                  ...(event.errorCode ? { errorCode: event.errorCode } : {}),
                  errorMessage: askErrorMessage(event.errorCode, event.message),
                };
              }
              return done;
            }),
          };
      }
    }
  }
}

/** The conversation as `ask.ask` carries it: answered text only, oldest first. */
function historyOf(messages: readonly AskChatMessage[]): AskMessage[] {
  const history: AskMessage[] = [];
  for (const message of messages) {
    if (message.role === "person") history.push({ role: "person", text: message.text.slice(0, ASK_MESSAGE_MAX) });
    else if (message.outcome === "answered" && message.text.trim().length > 0)
      history.push({ role: "ask", text: message.text.slice(0, ASK_MESSAGE_MAX) });
  }
  return history.slice(-200);
}

let seq = 0;
function nextId(prefix: string): string {
  seq += 1;
  return `${prefix}-${String(seq)}`;
}

function attachmentOf(image: HelpImageInput): HelpAttachment {
  return { id: nextId("ask-image"), image, previewUrl: `data:${image.mimeType};base64,${image.dataBase64}` };
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface AskConversationView {
  /** The engine's readiness, or null until it answers (`statusFailed` then reads as ready). */
  status: AskStatus | null;
  statusFailed: boolean;
  recheckStatus: () => void;
  conversationId: string;
  messages: AskChatMessage[];
  asking: boolean;
  draft: string;
  setDraft: (value: string) => void;
  attachments: HelpAttachment[];
  /** The last screenshot problem in plain words, or null. */
  attachError: string | null;
  addImageFiles: (files: readonly File[]) => Promise<void>;
  removeAttachment: (id: string) => void;
  /** Ask `draft` about `scope` with the person's roles and persona. */
  ask: (scope: AskScope, context: AskContext) => Promise<void>;
  stop: () => void;
  /** Close the conversation on this computer and clear the messages. */
  closeConversation: () => void;
}

export function useAskConversation(ask: AskToolsetBridge | null): AskConversationView {
  const [status, setStatus] = useState<AskStatus | null>(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [conversation, dispatch] = useReducer(askConversationReducer, undefined, () => ({
    conversationId: newHelpConversationId(),
    messages: [],
    activeAnswerId: null,
  }));
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState<HelpAttachment[]>([]);
  const attachmentsRef = useRef<HelpAttachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const statusSeq = useRef(0);
  const conversationIdRef = useRef(conversation.conversationId);
  conversationIdRef.current = conversation.conversationId;

  const setAttachmentList = useCallback((next: HelpAttachment[]) => {
    attachmentsRef.current = next;
    setAttachments(next);
  }, []);

  // Status and events.
  useEffect(() => {
    if (!ask) return undefined;
    let live = true;
    const unsubscribe = ask.onEvent((raw: unknown) => {
      if (!live) return;
      const parsed = askEventSchema.safeParse(raw);
      if (!parsed.success) {
        // A turn-finished event that does not parse must still end the turn.
        const loose = raw as { type?: unknown; conversationId?: unknown } | null;
        if (loose?.type === "turn-finished" && typeof loose.conversationId === "string") {
          dispatch({
            type: "event",
            event: { type: "turn-finished", conversationId: loose.conversationId, outcome: "failed", codeRefs: [], sources: [] },
          });
        }
        return;
      }
      const event = parsed.data;
      if (event.type === "status-changed") {
        statusSeq.current += 1;
        setStatus(event.status);
        return;
      }
      dispatch({ type: "event", event });
    });
    ask
      .getStatus()
      .then((raw: unknown) => {
        if (!live) return;
        const parsed = askStatusSchema.safeParse(raw);
        if (parsed.success) setStatus((current) => current ?? parsed.data);
        else setStatusFailed(true);
      })
      .catch(() => {
        if (live) setStatusFailed(true);
      });
    return () => {
      live = false;
      unsubscribe();
    };
  }, [ask]);

  const recheckStatus = useCallback(() => {
    if (!ask) return;
    const mine = ++statusSeq.current;
    ask
      .getStatus()
      .then((raw: unknown) => {
        const parsed = askStatusSchema.safeParse(raw);
        if (parsed.success && mine === statusSeq.current) setStatus(parsed.data);
      })
      .catch(() => undefined);
  }, [ask]);

  // Leaving the page closes the conversation (the app deletes its folder).
  useEffect(() => {
    if (!ask) return undefined;
    return () => {
      void ask.closeConversation({ conversationId: conversationIdRef.current }).catch(() => undefined);
    };
  }, [ask]);

  const asking = conversation.activeAnswerId !== null;

  const addImageFiles = useCallback(
    async (files: readonly File[]) => {
      for (const file of files) {
        if (attachmentsRef.current.length >= ASK_IMAGES_MAX) {
          setAttachError(ASK_IMAGE_LIMIT_MESSAGE);
          return;
        }
        if (!isHelpImageMimeType(file.type)) {
          setAttachError(ASK_IMAGE_TYPE_MESSAGE);
          continue;
        }
        if (file.size <= 0 || file.size > ASK_IMAGE_BYTES_MAX) {
          setAttachError(ASK_IMAGE_SIZE_MESSAGE);
          continue;
        }
        let dataBase64: string;
        try {
          dataBase64 = await readFileAsBase64(file);
        } catch {
          setAttachError("That screenshot could not be read. Try another one.");
          continue;
        }
        // Checked again after the read: another add may have filled the slots meanwhile.
        if (attachmentsRef.current.length >= ASK_IMAGES_MAX) {
          setAttachError(ASK_IMAGE_LIMIT_MESSAGE);
          return;
        }
        const name = (file.name.trim() || "screenshot").slice(0, 200);
        setAttachError(null);
        setAttachmentList([...attachmentsRef.current, attachmentOf({ name, mimeType: file.type, dataBase64 })]);
      }
    },
    [setAttachmentList],
  );

  const removeAttachment = useCallback(
    (id: string) => {
      setAttachError(null);
      setAttachmentList(attachmentsRef.current.filter((attachment) => attachment.id !== id));
    },
    [setAttachmentList],
  );

  const askQuestion = useCallback(
    async (scope: AskScope, context: AskContext) => {
      if (!ask || asking) return;
      const question = draft.trim();
      if (question.length === 0) return;
      const sent = attachmentsRef.current.slice(0, ASK_IMAGES_MAX);
      const person: AskPersonMessage = { id: nextId("ask-person"), role: "person", text: question, images: sent };
      const answerId = nextId("ask-answer");
      const input = {
        conversationId: conversation.conversationId,
        scope,
        question,
        images: sent.map((attachment) => attachment.image),
        context,
        messages: historyOf(conversation.messages),
      };
      dispatch({ type: "ask-started", person, answerId });
      setDraft("");
      setAttachError(null);
      setAttachmentList([]);

      const restore = () => {
        setDraft((current) => (current.length === 0 ? question : current));
        if (attachmentsRef.current.length === 0) setAttachmentList(sent);
      };
      const parsed = askAskInputSchema.safeParse(input);
      if (!parsed.success) {
        dispatch({
          type: "ask-failed",
          answerId,
          errorCode: "UNKNOWN",
          message: "That question could not be sent. Shorten it and ask again.",
        });
        restore();
        return;
      }
      try {
        const result = await ask.ask(parsed.data);
        if (!result.ok) {
          dispatch({
            type: "ask-failed",
            answerId,
            errorCode: result.errorCode,
            message: askErrorMessage(result.errorCode, result.message),
          });
          restore();
        }
      } catch {
        dispatch({ type: "ask-failed", answerId, errorCode: "UNKNOWN", message: askErrorMessage("UNKNOWN") });
        restore();
      }
    },
    [ask, asking, draft, conversation.conversationId, conversation.messages, setAttachmentList],
  );

  const stop = useCallback(() => {
    if (!ask || !asking) return;
    void ask.cancel({ conversationId: conversation.conversationId }).catch(() => undefined);
  }, [ask, asking, conversation.conversationId]);

  const closeConversation = useCallback(() => {
    const old = conversation.conversationId;
    if (ask) {
      if (asking) void ask.cancel({ conversationId: old }).catch(() => undefined);
      void ask.closeConversation({ conversationId: old }).catch(() => undefined);
    }
    dispatch({ type: "reset", conversationId: newHelpConversationId() });
    setDraft("");
    setAttachError(null);
    setAttachmentList([]);
  }, [ask, asking, conversation.conversationId, setAttachmentList]);

  return {
    status,
    statusFailed,
    recheckStatus,
    conversationId: conversation.conversationId,
    messages: conversation.messages,
    asking,
    draft,
    setDraft,
    attachments,
    attachError,
    addImageFiles,
    removeAttachment,
    ask: askQuestion,
    stop,
    closeConversation,
  };
}
